import { allImages, localImages } from '@root/assets';
import { readPawnColour } from '@root/lib/pawnColour';
import { phaserBridge } from './bridge';
import { SHARED_TEXTURES, colourTextures } from './textureKeys';

// Board layers are plain webp textures. Level lays them out using the
// coordinates in lib/boardPath.
export default class Preload extends Phaser.Scene {
  private loadErrorFired = false;

  constructor() {
    super('Preload');
  }

  preload(): void {
    this.cameras.main.setBackgroundColor('#230b38');

    // Report real progress. A retry adds a file and lowers the value, so
    // don't let the bar move backwards.
    let shown = 0;
    this.load.on('progress', (value: number) => {
      if (value <= shown) return;
      shown = value;
      phaserBridge.sendToReact({ type: 'LOAD_PROGRESS', value });
    });

    // CDN first, bundled copy as the fallback. Filenames are Vite-hashed, so
    // the generic CDN retry can't rewrite the path; use the local URL.
    // Phaser does not stop on a load error, so say so and don't start Level.
    // Otherwise the board quietly draws with holes in it.
    const retriedKeys = new Set<string>();
    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      if (retriedKeys.has(file.key)) {
        if (this.loadErrorFired) return;
        this.loadErrorFired = true;
        phaserBridge.sendToReact({
          type: 'SCENE_ERROR',
          scene: 'Preload',
          error: `Failed to load asset: ${file.key}`,
        });
        return;
      }
      const localSrc = localImages[file.key];
      if (!localSrc || localSrc === file.src) return;
      retriedKeys.add(file.key);
      this.load.image(file.key, localSrc);
      this.load.start();
    });

    // Only the colour the player last used. Level fetches another if they
    // switch, rather than us downloading all four up front.
    const keys = [...SHARED_TEXTURES, ...colourTextures(readPawnColour())];
    keys.forEach((key) => {
      this.load.image(key, allImages[key]);
    });
  }

  create(): void {
    if (this.loadErrorFired) return;
    phaserBridge.sendToReact({ type: 'LOAD_COMPLETE' });
    this.scene.start('Level');
  }
}
